import React, { useState } from 'react';
import { apiFetch } from '../lib/apiFetch';
import { ApiResponse, LlamadaVenta, Cliente } from '../types';
import { toast } from '../lib/toast';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

interface Props {
    clientes: Cliente[];
    onClose: () => void;
    onSubido: () => void;
}

export const SubirLlamadaVentaModal: React.FC<Props> = ({ clientes, onClose, onSubido }) => {
    const [clienteId, setClienteId] = useState('');
    const [productoServicio, setProductoServicio] = useState('');
    const [archivo, setArchivo] = useState<File | null>(null);
    const [subiendo, setSubiendo] = useState(false);

    const handleSubir = async () => {
        if (!clienteId || !archivo) {
            toast.error('Elegí un cliente y un archivo de audio');
            return;
        }
        setSubiendo(true);
        try {
            const form = new FormData();
            form.append('cliente_id', clienteId);
            if (productoServicio.trim()) form.append('producto_servicio', productoServicio.trim());
            form.append('archivo', archivo);
            const res = await apiFetch(`${API_BASE}/llamadas-venta`, { method: 'POST', body: form });
            const json: ApiResponse<LlamadaVenta> = await res.json();
            if (json.success) {
                toast.success('Llamada recibida, en cola para analizar');
                onSubido();
            } else {
                toast.error(json.error || 'Error al subir la llamada');
            }
        } catch (e) {
            toast.error('No se pudo conectar con el servidor');
        } finally {
            setSubiendo(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5" onClick={e => e.stopPropagation()}>
                <h3 className="text-base font-semibold text-slate-800 mb-4">Subir llamada de venta</h3>

                <label className="block text-xs font-medium text-slate-600 mb-1">Cliente</label>
                <select
                    value={clienteId}
                    onChange={e => setClienteId(e.target.value)}
                    className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 mb-3"
                >
                    <option value="">Elegí un cliente...</option>
                    {clientes.map(c => (
                        <option key={c.id} value={c.id}>{c.empresa}</option>
                    ))}
                </select>

                <label className="block text-xs font-medium text-slate-600 mb-1">Producto/servicio ofrecido (opcional)</label>
                <input
                    type="text"
                    value={productoServicio}
                    onChange={e => setProductoServicio(e.target.value)}
                    placeholder="Ej: Plan de suscripción anual"
                    className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 mb-3"
                />

                <label className="block text-xs font-medium text-slate-600 mb-1">Archivo (OGG, MP3, M4A, WAV, AAC — máx 50MB)</label>
                <input
                    type="file"
                    accept=".ogg,.mp3,.m4a,.wav,.aac"
                    onChange={e => setArchivo(e.target.files?.[0] || null)}
                    className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 mb-4"
                />

                <div className="flex justify-end gap-2">
                    <button onClick={onClose} className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-800">
                        Cancelar
                    </button>
                    <button
                        onClick={handleSubir}
                        disabled={subiendo}
                        className="px-3 py-1.5 text-sm bg-slate-800 text-white rounded-lg hover:bg-slate-700 disabled:opacity-50"
                    >
                        {subiendo ? 'Subiendo...' : 'Subir'}
                    </button>
                </div>
            </div>
        </div>
    );
};
